import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import '../styles/Blog.css';
import { searchBlogs } from '../services/api';
import Pagination from '../components/Pagination';
import NotFound from '../components/NotFound';
import { formatUrl } from '../utils/config';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BEARER_TOKEN = process.env.REACT_APP_BEARER_TOKEN;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Authorization: `Bearer ${BEARER_TOKEN}`,
    'Content-Type': 'application/json',
  },
});

// Placeholder for broken/missing images
const ImagePlaceholder = ({ height = '250px', icon = 'fa-newspaper' }) => (
  <div
    style={{
      width: '100%',
      height,
      background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '8px',
      minHeight: '200px',
    }}
  >
    <i className={`fal ${icon}`} style={{ fontSize: '3rem', color: '#bbb' }}></i>
  </div>
);

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});
  const [isSearching, setIsSearching] = useState(false);

  const handleImageError = (postId) => {
    setBrokenImages((prev) => ({ ...prev, [postId]: true }));
  };

  const BlogPostSkeleton = () => (
    <div className="col-md-6 col-lg-4">
      <div className="blog-card">
        <div className="blog-img"><Skeleton height={250} /></div>
        <div className="blog-content">
          <div className="blog-meta">
            <Skeleton width={80} style={{ marginRight: '10px' }} />
            <Skeleton width={80} />
          </div>
          <h3 className="box-title"><Skeleton width={200} /></h3>
          <Skeleton count={2} />
        </div>
      </div>
    </div>
  );

  const RecentPostSkeleton = () => (
    <div className="recent-post">
      <div className="media-img"><Skeleton width={100} height={100} /></div>
      <div className="media-body">
        <div className="recent-post-meta"><Skeleton width={80} /></div>
        <h4 className="post-title"><Skeleton width={150} /></h4>
      </div>
    </div>
  );

  const fetchBlogsWithPagination = async (page) => {
    try {
      const response = await api.get(`/api/v1/news/list?page=${page}`);
      if (response.data && response.data.success && response.data.payload) {
        setPosts(response.data.payload.page);
        setTotalPages(response.data.payload.totalPages);
        setCurrentPage(response.data.payload.currentPage);
        return response.data.payload.page;
      }
      throw new Error('Invalid API response structure');
    } catch (error) {
      console.error('Error fetching blogs:', error);
      if (error.response) console.error('API Response Error:', error.response.data);
      return [];
    }
  };

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(async () => {
      if (!value.trim()) {
        setIsSearching(false);
        fetchBlogsWithPagination(currentPage);
        return;
      }

      setIsSearching(true);
      setLoading(true);
      try {
        const results = await searchBlogs(value);
        // Extract the posts array from the response structure
        let postsArray = [];
        if (results && results.success && results.payload) {
          postsArray = results.payload.page || results.payload;
        } else if (Array.isArray(results)) {
          postsArray = results;
        }
        setPosts(postsArray);
        setTotalPages(1); // search usually returns single page
      } catch (error) {
        console.error('Error searching blogs:', error);
        setError('Search failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }, 500);

    setTypingTimeout(timeout);
  };

  useEffect(() => {
    const fetchBlogPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        const blogData = await fetchBlogsWithPagination(currentPage);
        if (blogData.length > 0) {
          const randomPosts = blogData.sort(() => 0.5 - Math.random()).slice(0, 3);
          setRecentPosts(randomPosts);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();

    return () => {
      if (typingTimeout) clearTimeout(typingTimeout);
    };
  }, [currentPage]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date unavailable';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Date unavailable';
    return date.toLocaleDateString();
  };

  const renderImage = (post, className = '', style = {}) => {
    const src = post.thumbnail;
    const postId = post.id;
    if (!src || brokenImages[postId]) {
      return <ImagePlaceholder height={style.height || '250px'} icon="fa-newspaper" />;
    }
    return (
      <img
        src={formatUrl(src)}
        alt={post.title || 'Blog image'}
        className={className}
        style={style}
        onError={() => handleImageError(postId)}
      />
    );
  };

  return (
    <>
      {/* Force black color for widget titles */}
      <style>{`.widget .widget_title { color: #000 !important; }`}</style>

      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">Blog Post</h1>
            <ul className="breadcumb-menu">
              <li><Link to="/">Home</Link></li>
              <li>Blog</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="th-blog-wrapper space-top space-extra2-bottom">
        <div className="container">
          <div className="row gx-40">
            <div className="col-xl-8 col-lg-7">
              <div className="blog-card-area">
                <div className="row">
                  {loading ? (
                    [...Array(6)].map((_, index) => <BlogPostSkeleton key={index} />)
                  ) : posts.length === 0 ? (
                    <NotFound message={isSearching ? "No results found." : "No blog posts found."} />
                  ) : (
                    posts.map((post) => (
                      <div className="col-md-6 col-lg-6" key={post.id}>
                        <div className="th-blog blog-single">
                          <div className="blog-img">
                            <Link to={`/blogdetails/${post.id}`} state={{ blogData: post }}>
                              {renderImage(post, 'img-fluid', { height: '250px', objectFit: 'cover' })}
                            </Link>
                          </div>
                          <div className="blog-content">
                            <div className="blog-meta">
                              <Link to={`/blogdetails/${post.id}`} state={{ blogData: post }}>
                                <i className="fa-solid fa-calendar-days" />
                                {formatDate(post.createdAt)}
                              </Link>
                            </div>
                            <h3 className="box-title">
                              <Link to={`/blogdetails/${post.id}`} state={{ blogData: post }}>
                                {post.title}
                              </Link>
                            </h3>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {!loading && !isSearching && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    window.scrollTo(0, 0);
                  }}
                />
              )}
            </div>

            <div className="col-xl-4 col-lg-5">
              <aside className="sidebar-area">
                <div className="widget widget_search">
                  <form className="search-form" onSubmit={(e) => e.preventDefault()}>
                    <input
                      type="text"
                      placeholder="Enter Keyword..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    {loading && (
                      <span className="position-absolute" style={{ right: '60px', top: '50%', transform: 'translateY(-50%)', zIndex: 4 }}>
                        <span className="spinner-border spinner-border-sm text-primary" />
                      </span>
                    )}
                    <button type="submit" disabled={loading}>
                      <i className="far fa-search"></i>
                    </button>
                  </form>
                </div>

                <div className="widget">
                  <h3 className="widget_title">Recent Posts</h3>
                  <div className="recent-post-wrap">
                    {loading
                      ? [...Array(3)].map((_, index) => <RecentPostSkeleton key={index} />)
                      : recentPosts.map((post) => (
                          <div key={post.id} className="recent-post">
                            <div className="media-img">
                              <Link to={`/blogdetails/${post.id}`} state={{ blogData: post }}>
                                {renderImage(post, '', { width: '100%', height: '100px', objectFit: 'cover' })}
                              </Link>
                            </div>
                            <div className="media-body">
                              <div className="recent-post-meta">
                                <Link to={`/blogdetails/${post.id}`} state={{ blogData: post }}>
                                  <i className="fa-solid fa-calendar-days" />
                                  {formatDate(post.createdAt)}
                                </Link>
                              </div>
                              <h4 className="post-title">
                                <Link className="text-inherit" to={`/blogdetails/${post.id}`} state={{ blogData: post }}>
                                  {post.title || 'No Title Available'}
                                </Link>
                              </h4>
                            </div>
                          </div>
                        ))}
                  </div>
                </div>

                <div className="widget widget_tag_cloud">
                  <h3 className="widget_title">Popular Tags</h3>
                  <div className="tagcloud">
                    <Link to="/blog">Construction</Link>
                    <Link to="/blog">Real Estate</Link>
                    <Link to="/blog">Property</Link>
                    <Link to="/blog">Land</Link>
                    <Link to="/blog">Offers</Link>
                    <Link to="/blog">News</Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Blog;